/**
 * THE GATE'S QUEUE — canon R10's two questions, as pure functions.
 *
 * The web package has no jsdom, so a component test can only assert what the server renders. That is
 * fine for "is the control offered", and useless for the rules that actually make the gate a gate:
 * that a second drop ADDS to the first rather than replacing it, that the same file dropped twice is
 * one file, and that nothing can be submitted until every document's type has been CONFIRMED by a
 * person. Those live here so they can be tested against real inputs in node.
 *
 * `deal-form.ts` and `upload.ts` are the precedent — the logic is pure, the component is the wiring.
 */

import { suggestDocumentType, type DocumentType } from './document-types.js';

/** The part of a `File` this module reasons about. Tests pass plain objects; the browser passes a `File`. */
export interface FileLike {
  name: string;
  size: number;
}

/**
 * One file waiting at the gate, with both answers it owes.
 *
 * Generic in the file type so the COMPONENT can hold real `File`s — the bytes have to survive the
 * queue to be read at submit — while the tests hold plain objects and need no DOM.
 */
export interface QueuedFile<F extends FileLike = FileLike> {
  file: F;
  /** What the FILENAME suggests, or null. A seed for the confirm chip, never a declaration. */
  suggested: DocumentType | null;
  /** What the person has settled on. Pre-filled from `suggested`; means nothing until `confirmed`. */
  documentType: string;
  /** Whether a person has accepted or replaced the guess. THIS is the declaration. */
  confirmed: boolean;
  lineage: 'new' | 'update';
  targetDocumentId: string;
}

/** Identity for de-duplication. Name alone would collide across revisions of the same filename. */
function key(f: FileLike): string {
  return `${f.name}:${f.size}`;
}

/**
 * Add files to the queue, keeping what is already there.
 *
 * 🔴 **APPENDS. The version this replaced REPLACED**, and the bug is invisible until R10's "bulk drops
 * supported" is real: drop the drawing set, answer its questions, then drop the budget — and the
 * drawings and everything you typed against them are gone. With one file at a time nobody would ever
 * see it.
 *
 * De-duped on name+size, so re-dropping a file already queued is a no-op rather than a double ingest.
 * A file already in the queue keeps the answers it has: re-dropping must not silently reset a
 * confirmation a person already made.
 */
export function addToQueue<F extends FileLike>(
  existing: readonly QueuedFile<F>[],
  incoming: readonly F[],
): QueuedFile<F>[] {
  const seen = new Set(existing.map((q) => key(q.file)));
  const merged = [...existing];
  for (const file of incoming) {
    if (seen.has(key(file))) continue;
    seen.add(key(file));
    const suggested = suggestDocumentType(file.name);
    merged.push({
      file,
      suggested,
      documentType: suggested ?? '',
      confirmed: false,
      lineage: 'new',
      targetDocumentId: '',
    });
  }
  return merged;
}

/** What still stands between this queue and the record. Empty means it can be submitted. */
export interface QueueBlockers {
  /** How many documents still have no confirmed type. R10's first question. */
  unconfirmed: number;
  /** Whether any update fails to name what it supersedes. R10's second question, R2's lineage. */
  missingTarget: boolean;
  /** Nothing queued at all — not an error, but not submittable either. */
  empty: boolean;
}

/**
 * ⚠️ `unconfirmed` counts `confirmed === false`, NOT `documentType === ''`.
 *
 * They differ in exactly the case this was built for: a file whose name suggested a type arrives with
 * the box already filled in. Keying on the box would treat that pre-fill as an answer, and the guess
 * would be saved to the record as though a person had declared it — a silent save with extra steps
 * (canon R6). The flag is set only by an explicit act, so a pre-filled box blocks the submit exactly
 * like an empty one.
 */
export function queueBlockers(queue: readonly QueuedFile<FileLike>[]): QueueBlockers {
  return {
    unconfirmed: queue.filter((q) => !q.confirmed).length,
    missingTarget: queue.some((q) => q.lineage === 'update' && q.targetDocumentId === ''),
    empty: queue.length === 0,
  };
}

/** Whether the queue may be sent. */
export function canSubmitQueue(queue: readonly QueuedFile<FileLike>[]): boolean {
  const b = queueBlockers(queue);
  return !b.empty && b.unconfirmed === 0 && !b.missingTarget;
}
